import { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, MapPin, MessageCircle, ArrowRight } from "lucide-react";
import { useReveal } from "../hooks/useReveal";
import { submitLead } from "../lib/api";
import { BRAND, PROJECT_TYPES, BUDGET_RANGES } from "../data/content";

const Contact = () => {
    const ref = useReveal();
    const [form, setForm] = useState({
        name: "",
        phone: "",
        email: "",
        project_type: "",
        budget: "",
        possession_date: "",
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.phone || !form.email) {
            toast.error("Please share your name, phone and email.");
            return;
        }
        setSubmitting(true);
        try {
            await submitLead({ ...form, source: "contact_form" });
            toast.success("Thank you. Sam will personally get back within 24 hours.");
            setSubmitted(true);
            setForm({
                name: "",
                phone: "",
                email: "",
                project_type: "",
                budget: "",
                possession_date: "",
                message: "",
            });
        } catch (err) {
            toast.error("Something went wrong. Please WhatsApp us instead.");
        } finally {
            setSubmitting(false);
        }
    };

    const waUrl = `https://wa.me/${BRAND.whatsapp}?text=${encodeURIComponent(
        "Hi Sam, I'd like to talk about my home."
    )}`;

    return (
        <div ref={ref} data-testid="contact-page" className="pt-24 md:pt-32">
            {/* Header */}
            <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-16 md:py-24">
                <div className="reveal max-w-4xl">
                    <p className="eyebrow mb-6">Begin Your Home</p>
                    <h1
                        className="font-display text-samora-ink leading-[0.95] tracking-tight"
                        style={{ fontSize: "clamp(2.75rem, 8vw, 8rem)" }}
                    >
                        Let's design<br />
                        <span className="italic font-serif text-samora-gold">
                            something quiet.
                        </span>
                    </h1>
                    <p className="mt-8 text-samora-text/75 text-base md:text-lg leading-relaxed max-w-2xl">
                        Every project starts with a 30-minute conversation.
                        Share a few details below and Sam will personally
                        respond within one working day.
                    </p>
                </div>
            </section>

            {/* Form + Sidebar */}
            <section className="pb-24 md:pb-32">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24">
                        {/* Form */}
                        <div className="lg:col-span-7 reveal">
                            {submitted ? (
                                <div
                                    data-testid="contact-success"
                                    className="border border-samora-gold p-10 md:p-14 bg-samora-sand/30"
                                >
                                    <p className="eyebrow mb-4">Received</p>
                                    <h2
                                        className="font-display text-samora-ink leading-tight"
                                        style={{
                                            fontSize:
                                                "clamp(2rem, 4vw, 3rem)",
                                        }}
                                    >
                                        Thank you.<br />
                                        <span className="italic font-serif text-samora-gold">
                                            Sam will be in touch.
                                        </span>
                                    </h2>
                                    <p className="mt-4 text-samora-text/75">
                                        We respond to every enquiry personally,
                                        within one working day.
                                    </p>
                                    <button
                                        onClick={() => setSubmitted(false)}
                                        className="btn-outline mt-8"
                                        data-testid="contact-reset"
                                    >
                                        Send Another
                                    </button>
                                </div>
                            ) : (
                                <form
                                    onSubmit={onSubmit}
                                    data-testid="contact-form"
                                    className="space-y-10"
                                >
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                        <div>
                                            <label className="samora-label">
                                                Full Name *
                                            </label>
                                            <input
                                                data-testid="contact-name"
                                                className="samora-input"
                                                placeholder="Your name"
                                                value={form.name}
                                                onChange={(e) =>
                                                    set("name", e.target.value)
                                                }
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="samora-label">
                                                Phone *
                                            </label>
                                            <input
                                                data-testid="contact-phone"
                                                className="samora-input"
                                                placeholder="+91"
                                                value={form.phone}
                                                onChange={(e) =>
                                                    set(
                                                        "phone",
                                                        e.target.value
                                                    )
                                                }
                                                required
                                            />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="samora-label">
                                                Email *
                                            </label>
                                            <input
                                                data-testid="contact-email"
                                                type="email"
                                                className="samora-input"
                                                placeholder="you@example.com"
                                                value={form.email}
                                                onChange={(e) =>
                                                    set(
                                                        "email",
                                                        e.target.value
                                                    )
                                                }
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="samora-label">
                                                Project Type
                                            </label>
                                            <select
                                                data-testid="contact-project-type"
                                                className="samora-input bg-transparent"
                                                value={form.project_type}
                                                onChange={(e) =>
                                                    set(
                                                        "project_type",
                                                        e.target.value
                                                    )
                                                }
                                            >
                                                <option value="">
                                                    Select…
                                                </option>
                                                {PROJECT_TYPES.map((t) => (
                                                    <option key={t} value={t}>
                                                        {t}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="samora-label">
                                                Budget Range
                                            </label>
                                            <select
                                                data-testid="contact-budget"
                                                className="samora-input bg-transparent"
                                                value={form.budget}
                                                onChange={(e) =>
                                                    set(
                                                        "budget",
                                                        e.target.value
                                                    )
                                                }
                                            >
                                                <option value="">
                                                    Select…
                                                </option>
                                                {BUDGET_RANGES.map((b) => (
                                                    <option key={b} value={b}>
                                                        {b}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="samora-label">
                                                Possession Date
                                            </label>
                                            <input
                                                data-testid="contact-possession"
                                                type="date"
                                                className="samora-input"
                                                value={form.possession_date}
                                                onChange={(e) =>
                                                    set(
                                                        "possession_date",
                                                        e.target.value
                                                    )
                                                }
                                            />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="samora-label">
                                                A little about your home
                                            </label>
                                            <textarea
                                                data-testid="contact-message"
                                                rows={4}
                                                className="samora-input resize-none"
                                                placeholder="Tell us about the space, the family, the timeline…"
                                                value={form.message}
                                                onChange={(e) =>
                                                    set(
                                                        "message",
                                                        e.target.value
                                                    )
                                                }
                                            />
                                        </div>
                                    </div>

                                    <button
                                        type="submit"
                                        data-testid="contact-submit"
                                        disabled={submitting}
                                        className="btn-samora disabled:opacity-60"
                                    >
                                        {submitting
                                            ? "Sending…"
                                            : "Send Enquiry"}
                                        <ArrowRight size={16} />
                                    </button>
                                </form>
                            )}
                        </div>

                        {/* Sidebar */}
                        <div className="lg:col-span-5 space-y-10 reveal">
                            <div className="bg-samora-ink text-samora-bone p-8 md:p-10">
                                <p className="eyebrow text-samora-gold mb-4">
                                    The Studio
                                </p>
                                <h3
                                    className="font-display leading-tight"
                                    style={{
                                        fontSize: "clamp(1.75rem, 3vw, 2.5rem)",
                                    }}
                                >
                                    Whitefield, Bengaluru
                                </h3>
                                <div className="mt-8 space-y-5">
                                    <a
                                        href={`tel:${BRAND.phoneRaw}`}
                                        data-testid="contact-phone-link"
                                        className="flex items-start gap-4 group"
                                    >
                                        <Phone
                                            size={18}
                                            className="text-samora-gold mt-1 flex-shrink-0"
                                            strokeWidth={1.5}
                                        />
                                        <div>
                                            <p className="eyebrow text-samora-bone/60 mb-1">
                                                Call
                                            </p>
                                            <p className="font-display text-xl group-hover:text-samora-gold transition-colors">
                                                {BRAND.phone}
                                            </p>
                                        </div>
                                    </a>
                                    <a
                                        href={`mailto:${BRAND.email}`}
                                        data-testid="contact-email-link"
                                        className="flex items-start gap-4 group"
                                    >
                                        <Mail
                                            size={18}
                                            className="text-samora-gold mt-1 flex-shrink-0"
                                            strokeWidth={1.5}
                                        />
                                        <div>
                                            <p className="eyebrow text-samora-bone/60 mb-1">
                                                Write
                                            </p>
                                            <p className="font-display text-xl group-hover:text-samora-gold transition-colors">
                                                {BRAND.email}
                                            </p>
                                        </div>
                                    </a>
                                    <a
                                        href={waUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        data-testid="contact-whatsapp-link"
                                        className="flex items-start gap-4 group"
                                    >
                                        <MessageCircle
                                            size={18}
                                            className="text-samora-gold mt-1 flex-shrink-0"
                                            strokeWidth={1.5}
                                        />
                                        <div>
                                            <p className="eyebrow text-samora-bone/60 mb-1">
                                                Chat
                                            </p>
                                            <p className="font-display text-xl group-hover:text-samora-gold transition-colors">
                                                WhatsApp Studio
                                            </p>
                                        </div>
                                    </a>
                                    <div className="flex items-start gap-4">
                                        <MapPin
                                            size={18}
                                            className="text-samora-gold mt-1 flex-shrink-0"
                                            strokeWidth={1.5}
                                        />
                                        <div>
                                            <p className="eyebrow text-samora-bone/60 mb-1">
                                                Visit (by appointment)
                                            </p>
                                            <p className="text-samora-bone/85 leading-relaxed">
                                                {BRAND.address}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <p className="eyebrow mb-4">Studio Hours</p>
                                <div className="border-t border-samora-ink/15 pt-4 space-y-3 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-samora-text/70">
                                            Monday – Saturday
                                        </span>
                                        <span className="text-samora-ink">
                                            10.30 – 19.00
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-samora-text/70">
                                            Sunday
                                        </span>
                                        <span className="text-samora-ink">
                                            By appointment
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Map */}
            <section
                data-testid="contact-map-section"
                className="w-full"
            >
                <iframe
                    title="Samora Spaces studio location"
                    src={BRAND.mapEmbed}
                    className="w-full h-[480px] md:h-[560px] grayscale"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                />
            </section>
        </div>
    );
};

export default Contact;
